// views/Dashboard/PainelExecutivo/sections/financeiro/SecaoFinanceiro.tsx
import React, { useCallback, useMemo, useState } from 'react';
import type { FiltrosDashboard } from '../../types';
import { rotuloPeriodoDashboard } from '../../../../../src/lib/dashboard/periodoDashboard';
import { filtrarFaturasPorPeriodo, ROTULOS_BASE_DATA } from '../../../../../src/lib/dashboard/filtrarFaturasPorPeriodo';
import { calcularKpis } from '../../../../../src/lib/financeiro/calcularKpis';
import { calcularAging } from '../../../../../src/lib/financeiro/calcularAging';
import { calcularPontosMensais } from '../../../../../src/lib/financeiro/calcularPontosMensais';
import { DEFINICOES_KPI } from '../../../../../src/lib/financeiro/definicoesKpi';
import type { IdKpi } from '../../../../../src/lib/financeiro/definicoesKpi';
import { parteVigorDaFatura } from '../../../../../src/lib/financeiro/parteVigor';
import type { FaturaPainel } from '../../../../../src/lib/financeiro/types';
import { useToast } from '../../../../../src/contexts/ToastContext';
import { useIndiceNomesUc } from '../../../../../src/hooks/useIndiceNomesUc';
import { useFinanceiroDashboardData } from './useFinanceiroDashboardData';
import LoadingSpinner from '../../../../../src/components/LoadingSpinner';
import KpiCards from '../../../../Financeiro/PainelExecutivo/components/KpiCards';
import ListaBoletosDrawer from '../../../../Financeiro/PainelExecutivo/components/ListaBoletosDrawer';
import { useAcoesBoleto } from '../../../../Financeiro/PainelExecutivo/hooks/useAcoesBoleto';
import { planilhaBoletos, planilhaSobreLista, slugArquivo } from '../../../../Financeiro/PainelExecutivo/lib/planilhasPainel';
import { baixarPlanilhas } from '../../../../Financeiro/PainelExecutivo/lib/baixarPlanilhas';
import UCManagementModal from '../../../../Financeiro/UCManagementModal';
import EvolucaoMensalChart from '../../../../Financeiro/PainelExecutivo/components/EvolucaoMensalChart';
import EvolucaoInadimplenciaChart from '../../../../Financeiro/PainelExecutivo/components/EvolucaoInadimplenciaChart';
import BoletosPorStatusDonut from '../../../../Financeiro/PainelExecutivo/components/BoletosPorStatusDonut';
import AgingTable from '../../../../Financeiro/PainelExecutivo/components/AgingTable';
import ModalGraficoExpandido from '../../components/ModalGraficoExpandido';
import ComparativoResumo from '../../components/ComparativoResumo';

interface Props {
  filtros: FiltrosDashboard;
}

type GraficoId = 'evolucaoMensal' | 'evolucaoInadimplencia' | 'boletosPorStatus' | 'aging';

const TITULOS: Record<GraficoId, string> = {
  evolucaoMensal: 'Evolução Financeira Mensal',
  evolucaoInadimplencia: 'Evolução da Inadimplência',
  boletosPorStatus: 'Boletos por Status',
  aging: 'Aging da Inadimplência',
};

const fmtBRL = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPct = (v: number) => `${(v * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;

const MiniCardClicavel: React.FC<{ onClick: () => void; children: React.ReactNode }> = ({ onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    className="text-left bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 hover:ring-2 hover:ring-primary/40 transition-shadow w-full"
  >
    {children}
  </button>
);

const SecaoFinanceiro: React.FC<Props> = ({ filtros }) => {
  const { dados, percentualVigorPorParceira, loading, erro, refetch } = useFinanceiroDashboardData();
  const { addToast } = useToast();
  const [graficoAberto, setGraficoAberto] = useState<GraficoId | null>(null);
  const [kpiAberto, setKpiAberto] = useState<IdKpi | null>(null);
  const [exportandoLista, setExportandoLista] = useState(false);
  const { ucAberta, setUcAberta, abrindoUcId, abrindoBoletoId, abrirUc, abrirBoleto } = useAcoesBoleto();
  // Assinante e titular da conta de luz — o cadastro da UC é a única fonte do titular.
  const nomes = useIndiceNomesUc();
  const hoje = useMemo(() => new Date(), []);

  const filtrados = useMemo(
    () => filtrarFaturasPorPeriodo(dados, filtros.periodo, hoje, filtros.baseDataFinanceiro),
    [dados, filtros.periodo, hoje, filtros.baseDataFinanceiro],
  );
  const kpis = useMemo(() => calcularKpis(filtrados, hoje), [filtrados, hoje]);
  const aging = useMemo(() => calcularAging(filtrados, hoje), [filtrados, hoje]);
  // A série usa a MESMA base do filtro. Com o filtro por vencimento e a série
  // por referência, o último ponto podia cair fora do período — era isso que
  // fazia o mini-card dizer "emitido em NOV/2026" com setembro selecionado.
  const pontosMensais = useMemo(
    () => calcularPontosMensais(filtrados, filtros.baseDataFinanceiro),
    [filtrados, filtros.baseDataFinanceiro],
  );
  const rotulo = useMemo(() => rotuloPeriodoDashboard(filtros.periodo), [filtros.periodo]);

  const kpisComparacao = useMemo(() => {
    if (!filtros.periodoComparacao) return null;
    const filtradosB = filtrarFaturasPorPeriodo(dados, filtros.periodoComparacao, hoje, filtros.baseDataFinanceiro);
    return calcularKpis(filtradosB, hoje);
  }, [dados, filtros.periodoComparacao, hoje, filtros.baseDataFinanceiro]);

  // A lista do card sai da MESMA definição que calculou o número do card, sobre
  // o MESMO recorte de período — o total da lista é o do card por construção.
  // Com comparação ligada, é o período principal: é ele que o card mostra.
  const faturasDoCard = useMemo(
    () => (kpiAberto ? DEFINICOES_KPI[kpiAberto].filtrar(filtrados) : []),
    [kpiAberto, filtrados],
  );
  const parteVigor = useCallback(
    (f: FaturaPainel) => parteVigorDaFatura(f, percentualVigorPorParceira),
    [percentualVigorPorParceira],
  );
  const fecharLista = useCallback(() => setKpiAberto(null), []);

  const exportarLista = async (faturasVisiveis: FaturaPainel[]) => {
    if (!kpiAberto) return;
    const def = DEFINICOES_KPI[kpiAberto];
    setExportandoLista(true);
    try {
      // Este painel só recorta por período: UG, status e cliente não são filtros
      // dele. A base de data entra junto do período — sem ela, duas planilhas do
      // mesmo mês com bases diferentes ficariam indistinguíveis.
      const filtrosDescritos = {
        periodo: `${rotulo} (por ${ROTULOS_BASE_DATA[filtros.baseDataFinanceiro].toLowerCase()})`,
        unidadeGeradora: 'Todas as UGs',
        status: 'Todos',
        cliente: '',
      };
      await baixarPlanilhas([
        planilhaBoletos(faturasVisiveis, 'Boletos', parteVigor, nomes),
        planilhaSobreLista(def.titulo, def.descricaoLista, filtrosDescritos, new Date()),
      ], `boletos_${slugArquivo(def.titulo)}_${slugArquivo(rotulo)}`);
    } catch (e: any) {
      console.error('Erro ao exportar a lista de boletos:', e);
      addToast(e?.message || 'Não foi possível gerar o Excel.', 'error');
    } finally {
      setExportandoLista(false);
    }
  };

  if (erro) {
    return <p className="text-sm font-semibold text-red-600 dark:text-red-400">{erro}</p>;
  }

  if (loading && dados.length === 0) {
    return <LoadingSpinner label="Carregando seção Financeiro..." />;
  }

  return (
    <div className="space-y-4">
      {/* O recorte fica escrito por extenso: o mesmo período em bases diferentes
          devolve conjuntos diferentes de faturas, e o número do card muda junto. */}
      <p className="text-xs text-gray-500 dark:text-gray-400">
        <strong className="text-gray-700 dark:text-gray-200">{rotulo}</strong>, por{' '}
        {ROTULOS_BASE_DATA[filtros.baseDataFinanceiro].toLowerCase()} ·{' '}
        <span className="text-gray-400">{filtrados.length.toLocaleString('pt-BR')} faturas no recorte</span>
      </p>
      <KpiCards kpis={kpis} variacaoVencidoPct={null} rotuloPeriodo={rotulo} onAbrir={setKpiAberto} />

      {kpisComparacao && filtros.periodoComparacao && (
        <ComparativoResumo
          rotuloComparacao={rotuloPeriodoDashboard(filtros.periodoComparacao)}
          itens={[
            { titulo: 'Faturamento Emitido', valorAtual: kpis.emitido, valorComparacao: kpisComparacao.emitido, formatar: fmtBRL },
            { titulo: 'Recebido', valorAtual: kpis.recebido, valorComparacao: kpisComparacao.recebido, formatar: fmtBRL },
            { titulo: 'Em Aberto', valorAtual: kpis.emAberto, valorComparacao: kpisComparacao.emAberto, formatar: fmtBRL },
            { titulo: 'Vencido', valorAtual: kpis.vencido, valorComparacao: kpisComparacao.vencido, formatar: fmtBRL },
            { titulo: 'Taxa de Inadimplência', valorAtual: kpis.taxaInadimplencia, valorComparacao: kpisComparacao.taxaInadimplencia, formatar: fmtPct },
            { titulo: 'Ticket Médio', valorAtual: kpis.ticketMedio, valorComparacao: kpisComparacao.ticketMedio, formatar: fmtBRL },
          ]}
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <MiniCardClicavel onClick={() => setGraficoAberto('evolucaoMensal')}>
          <EvolucaoMensalChart pontos={pontosMensais} modoCompacto />
        </MiniCardClicavel>
        <MiniCardClicavel onClick={() => setGraficoAberto('evolucaoInadimplencia')}>
          <EvolucaoInadimplenciaChart pontos={pontosMensais} modoCompacto />
        </MiniCardClicavel>
        <MiniCardClicavel onClick={() => setGraficoAberto('boletosPorStatus')}>
          <BoletosPorStatusDonut dados={filtrados} modoCompacto />
        </MiniCardClicavel>
        <MiniCardClicavel onClick={() => setGraficoAberto('aging')}>
          <AgingTable faixas={aging} modoCompacto />
        </MiniCardClicavel>
      </div>

      {graficoAberto && (
        <ModalGraficoExpandido titulo={TITULOS[graficoAberto]} onClose={() => setGraficoAberto(null)}>
          {graficoAberto === 'evolucaoMensal' && <EvolucaoMensalChart pontos={pontosMensais} />}
          {graficoAberto === 'evolucaoInadimplencia' && <EvolucaoInadimplenciaChart pontos={pontosMensais} />}
          {graficoAberto === 'boletosPorStatus' && <BoletosPorStatusDonut dados={filtrados} />}
          {graficoAberto === 'aging' && <AgingTable faixas={aging} />}
        </ModalGraficoExpandido>
      )}

      <ListaBoletosDrawer
        layout="modal"
        aberto={!!kpiAberto}
        titulo={kpiAberto ? DEFINICOES_KPI[kpiAberto].titulo : ''}
        descricao={kpiAberto ? `${DEFINICOES_KPI[kpiAberto].descricaoLista} · ${rotulo}` : ''}
        faturas={faturasDoCard}
        hoje={hoje}
        mostrarDias={kpiAberto === 'prazoMedio' || kpiAberto === 'recuperado'}
        permitirFiltro7Dias={kpiAberto === 'aVencer'}
        escAtivo={!ucAberta}
        abrindoUcId={abrindoUcId}
        abrindoBoletoId={abrindoBoletoId}
        exportando={exportandoLista}
        onFechar={fecharLista}
        onAbrirUc={abrirUc}
        onAbrirBoleto={abrirBoleto}
        onExportar={exportarLista}
        parteVigor={parteVigor}
        nomes={nomes}
      />

      <UCManagementModal
        isOpen={!!ucAberta}
        onClose={() => setUcAberta(null)}
        unidade={ucAberta}
        abaInicial="historico_faturas"
        onSave={() => { setUcAberta(null); refetch(); }}
      />
    </div>
  );
};

export default SecaoFinanceiro;
